/* eslint-disable react/no-unstable-nested-components */

import {
  ArchiveBoxIcon,
  ArrowsUpDownIcon,
  MagnifyingGlassIcon,
  StarIcon,
} from '@heroicons/react/20/solid';
import {
  ArrowDownTrayIcon,
  AdjustmentsVerticalIcon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon,
  StarIcon as StarOutlineIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { PaginationControls } from '@/components/common/PaginationControls';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  ReadTemplateSummary,
  TemplateListApiArg,
} from '@/services/api/generated/templatesV2Service';

import {
  CATEGORY_OPTIONS,
  FORMAT_OPTIONS,
  GLOBAL_FIRM_FILTER_VALUE,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_FORMAT_COLORS,
  TEMPLATE_FORMAT_LABELS,
} from './constants';
import { NewTemplateMenu } from './NewTemplateMenu';
import { TemplateRowPreviewCard } from './TemplateRowPreviewCard';

const COLUMN_SIZING_STORAGE_KEY = 'templates-v2:column-sizing';
const ALL_FILTER_VALUE = '__ALL__';

interface SelectOption {
  value: string;
  label: string;
}

interface TemplatesOperatorTableProps {
  title: string;
  subtitle: string;
  data: ReadTemplateSummary[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  searchValue: string;
  archivedOnly: boolean;
  selectedRows: Set<string>;
  sortField?: string;
  sortDirection: 'asc' | 'desc';
  firmFilterValues: string[];
  firmOptions: SelectOption[];
  scopeFilterValue?: TemplateListApiArg['scope'];
  categoryFilterValues: NonNullable<TemplateListApiArg['category']>[];
  formatFilterValues: NonNullable<TemplateListApiArg['templateFormat']>[];
  defaultFilterValue?: boolean;
  isBulkArchiving: boolean;
  canClearFilters?: boolean;
  firmId?: string;
  onSearchChange: (value: string) => void;
  onArchivedOnlyChange: (archivedOnly: boolean) => void;
  onClearFilters?: () => void;
  onSelectionChange: (selectedRows: Set<string>) => void;
  onBulkArchiveClick: () => void;
  onSortChange: (
    field: string | undefined,
    direction?: 'asc' | 'desc',
  ) => void;
  onScopeFilterChange?: (
    scope: TemplateListApiArg['scope'] | undefined,
  ) => void;
  onFirmFilterChange: (values: string[]) => void;
  onCategoryFilterChange: (
    values: NonNullable<TemplateListApiArg['category']>[],
  ) => void;
  onFormatFilterChange: (
    values: NonNullable<TemplateListApiArg['templateFormat']>[],
  ) => void;
  onDefaultFilterChange: (value: boolean | undefined) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRowClick: (row: ReadTemplateSummary) => void;
  onBulkDownloadClick: () => void;
  onToggleDefault?: (row: ReadTemplateSummary) => void;
  firmNames: Record<string, string>;
  detailSearch?: string;
  isBulkDownloading?: boolean;
}

interface HeaderSortProps {
  label: string;
  sortKey?: string;
  sortField?: string;
  sortDirection: 'asc' | 'desc';
  onSortChange: (
    field: string | undefined,
    direction?: 'asc' | 'desc',
  ) => void;
  filterControl?: ReactNode;
}

interface HeaderMultiFilterProps {
  label: string;
  options: SelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
}

const readStoredColumnSizing = (): ColumnSizingState => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(COLUMN_SIZING_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as ColumnSizingState)
      : {};
  } catch {
    return {};
  }
};

const renderDate = (value: string | null | undefined): ReactNode => {
  if (!value) {
    return <span className="text-zinc-400">-</span>;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return <span className="text-zinc-400">-</span>;
  }

  return (
    <span className="tabular-nums text-zinc-700 dark:text-zinc-300">
      {parsedDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}
    </span>
  );
};

const renderFormatBadge = (format: string): ReactNode => {
  const label = TEMPLATE_FORMAT_LABELS[format] ?? format;
  const color =
    TEMPLATE_FORMAT_COLORS[format] ?? 'bg-zinc-100 text-zinc-700';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        color,
      )}
    >
      {label}
    </span>
  );
};

const normalizeTemplateStatus = (
  status: string | null | undefined,
): string => String(status ?? '');

const isArchivedLikeStatus = (status: string | null | undefined): boolean => {
  const normalized = normalizeTemplateStatus(status);
  return normalized === 'ARCHIVED' || normalized === 'SUPERSEDED';
};

const getArchivedStatusLabel = (status: string | null | undefined): string => {
  const normalized = normalizeTemplateStatus(status);
  if (normalized === 'SUPERSEDED') {
    return 'Superseded';
  }
  return 'Archived';
};

const isGlobalTemplate = (
  template: Pick<ReadTemplateSummary, 'scope'>,
): boolean => {
  const normalizedScope = String(template.scope ?? '');
  return normalizedScope === 'GLOBAL';
};

const getTemplateDetailsPath = (
  template: Pick<ReadTemplateSummary, 'id'>,
  search?: string,
): string => {
  const normalizedSearch = search
    ? (search.startsWith('?') ? search : `?${search}`)
    : '';
  return `/templates-v2/${String(template.id)}${normalizedSearch}`;
};

const SelectionCheckbox = ({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      aria-label={ariaLabel}
      className="h-4 w-4 cursor-pointer rounded border-zinc-300 text-blue-600 focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900"
      data-row-action="true"
    />
  );
};

const HeaderMultiFilter = ({
  label,
  options,
  selectedValues,
  onChange,
  searchable = false,
}: HeaderMultiFilterProps) => {
  const [open, setOpen] = useState(false);

  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const selectedLabel = useMemo(() => {
    if (selectedValues.length === 0) {
      return `All ${label}`;
    }

    if (selectedValues.length === 1) {
      return options.find((option) => option.value === selectedValues[0])?.label || `All ${label}`;
    }

    const noun = label.toLowerCase();
    return `${selectedValues.length} ${noun}s selected`;
  }, [label, options, selectedValues]);

  const toggleValue = useCallback(
    (value: string) => {
      if (value === ALL_FILTER_VALUE) {
        onChange([]);
        return;
      }

      const nextSelected = new Set(selectedValues);
      if (nextSelected.has(value)) {
        nextSelected.delete(value);
      } else {
        nextSelected.add(value);
      }

      // Keep output order aligned to options so URL/query arg ordering is stable.
      onChange(
        options
          .map((option) => option.value)
          .filter((optionValue) => nextSelected.has(optionValue)),
      );
    },
    [onChange, options, selectedValues],
  );

  const optionsWithAll = useMemo(
    () => [{ value: ALL_FILTER_VALUE, label: `All ${label}` }, ...options],
    [label, options],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-zinc-400 transition-colors hover:border-zinc-200 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:border-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
                selectedValues.length > 0 &&
                  'border-blue-200 bg-blue-50 text-blue-600 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25',
              )}
              aria-label={`Filter ${label}`}
              data-row-action="true"
            >
              <span className="relative inline-flex items-center justify-center">
                <AdjustmentsVerticalIcon className="h-3.5 w-3.5" />
                {selectedValues.length > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                )}
              </span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{selectedLabel}</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          {searchable && <CommandInput placeholder={`Search ${label}...`} />}
          <CommandList>
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup>
              {optionsWithAll.map((option) => {
                const isChecked =
                  option.value === ALL_FILTER_VALUE
                    ? selectedValues.length === 0
                    : selectedValueSet.has(option.value);

                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      toggleValue(option.value);
                    }}
                  >
                    <span
                      className={cn(
                        'mr-2 inline-flex h-4 w-4 items-center justify-center rounded border border-zinc-300',
                        isChecked && 'border-blue-500 bg-blue-500 text-white',
                      )}
                    >
                      {isChecked ? '✓' : null}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const HeaderSingleFilter = ({
  label,
  options,
  selectedValue,
  onChange,
  searchable = false,
}: {
  label: string;
  options: SelectOption[];
  selectedValue: string | null;
  onChange: (value: string | null) => void;
  searchable?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const optionsWithAll = useMemo(
    () => [{ value: ALL_FILTER_VALUE, label: `All ${label}` }, ...options],
    [label, options],
  );

  const selectedLabel = useMemo(() => {
    if (!selectedValue) {
      return `All ${label}`;
    }

    return (
      optionsWithAll.find((option) => option.value === selectedValue)?.label ||
      `All ${label}`
    );
  }, [label, optionsWithAll, selectedValue]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-zinc-400 transition-colors hover:border-zinc-200 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:border-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
                selectedValue &&
                  'border-blue-200 bg-blue-50 text-blue-600 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/25',
              )}
              aria-label={`Filter ${label}`}
              data-row-action="true"
            >
              <span className="relative inline-flex items-center justify-center">
                <AdjustmentsVerticalIcon className="h-3.5 w-3.5" />
                {selectedValue && (
                  <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                )}
              </span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{selectedLabel}</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          {searchable && <CommandInput placeholder={`Search ${label}...`} />}
          <CommandList>
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup>
              {optionsWithAll.map((option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => {
                    onChange(
                      option.value === ALL_FILTER_VALUE ? null : option.value,
                    );
                    setOpen(false);
                  }}
                >
                  <span
                    className={cn(
                      'mr-2 inline-flex h-4 w-4 items-center justify-center rounded border border-zinc-300',
                      (selectedValue ?? ALL_FILTER_VALUE) === option.value &&
                        'border-blue-500 bg-blue-500 text-white',
                    )}
                  >
                    {(selectedValue ?? ALL_FILTER_VALUE) === option.value
                      ? '✓'
                      : null}
                  </span>
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const HeaderSort = ({
  label,
  sortKey,
  sortField,
  sortDirection,
  onSortChange,
  filterControl,
}: HeaderSortProps) => {
  const isSorted = sortField === sortKey;

  const handleSort = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!sortKey) {
      return;
    }

    if (!isSorted) {
      onSortChange(sortKey, 'asc');
      return;
    }

    if (sortDirection === 'asc') {
      onSortChange(sortKey, 'desc');
      return;
    }

    onSortChange(undefined);
  };

  let SortIcon = ArrowsUpDownIcon;
  if (isSorted) {
    SortIcon = sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
        {label}
      </span>
      {filterControl}
      {sortKey && (
        <button
          type="button"
          onClick={handleSort}
          aria-label={`Sort ${label}`}
          className={cn(
            'ml-auto rounded p-0.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
            isSorted && 'text-blue-600 dark:text-blue-400',
          )}
          data-row-action="true"
        >
          <SortIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const DEFAULT_FILTER_OPTIONS: SelectOption[] = [
  { value: 'true', label: 'Default only' },
  { value: 'false', label: 'Not default' },
];

export const TemplatesOperatorTable = ({
  title,
  subtitle,
  data,
  isLoading,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  searchValue,
  archivedOnly,
  selectedRows,
  sortField,
  sortDirection,
  firmFilterValues,
  firmOptions,
  scopeFilterValue,
  categoryFilterValues,
  formatFilterValues,
  defaultFilterValue,
  isBulkArchiving,
  canClearFilters,
  firmId,
  onSearchChange,
  onArchivedOnlyChange,
  onClearFilters,
  onSelectionChange,
  onBulkArchiveClick,
  onSortChange,
  onScopeFilterChange,
  onFirmFilterChange,
  onCategoryFilterChange,
  onFormatFilterChange,
  onDefaultFilterChange,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  onBulkDownloadClick,
  onToggleDefault,
  firmNames,
  detailSearch,
  isBulkDownloading,
}: TemplatesOperatorTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    readStoredColumnSizing(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      COLUMN_SIZING_STORAGE_KEY,
      JSON.stringify(columnSizing),
    );
  }, [columnSizing]);

  const categoryFilterOptions = useMemo<SelectOption[]>(
    () => CATEGORY_OPTIONS,
    [],
  );
  const formatFilterOptions = useMemo<SelectOption[]>(
    () => FORMAT_OPTIONS,
    [],
  );

  type ActiveFilterChip = {
    key: string;
    label: string;
    onClear: () => void;
  };

  const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];

    const trimmedSearch = searchValue.trim();
    if (trimmedSearch) {
      chips.push({
        key: 'search',
        label: `Search: ${trimmedSearch}`,
        onClear: () => onSearchChange(''),
      });
    }

    if (archivedOnly) {
      chips.push({
        key: 'archived',
        label: 'Show archived',
        onClear: () => onArchivedOnlyChange(false),
      });
    }

    if (firmFilterValues.length > 0) {
      firmFilterValues.forEach((firmFilterValue) => {
        if (firmFilterValue === GLOBAL_FIRM_FILTER_VALUE) {
          chips.push({
            key: `firm:${firmFilterValue}`,
            label: 'Global (System)',
            onClear: () =>
              onFirmFilterChange(
                firmFilterValues.filter((value) => value !== firmFilterValue),
              ),
          });
          return;
        }

        chips.push({
          key: `firm:${firmFilterValue}`,
          label: `Firm: ${
            firmNames[firmFilterValue] ?? String(firmFilterValue)
          }`,
          onClear: () =>
            onFirmFilterChange(
              firmFilterValues.filter((value) => value !== firmFilterValue),
            ),
        });
      });
    } else if (scopeFilterValue === 'GLOBAL') {
      chips.push({
        key: 'scope',
        label: 'Scope: Global',
        onClear: () => onScopeFilterChange?.(undefined),
      });
    } else if (scopeFilterValue === 'FIRM') {
      chips.push({
        key: 'scope',
        label: 'Scope: Firm',
        onClear: () => onScopeFilterChange?.(undefined),
      });
    }

    categoryFilterValues.forEach((categoryFilterValue) => {
      const normalizedCategory = String(categoryFilterValue);
      chips.push({
        key: `category:${categoryFilterValue}`,
        label: `Category: ${
          TEMPLATE_CATEGORY_LABELS[normalizedCategory] ?? categoryFilterValue
        }`,
        onClear: () =>
          onCategoryFilterChange(
            categoryFilterValues.filter((value) => value !== categoryFilterValue),
          ),
      });
    });

    formatFilterValues.forEach((formatFilterValue) => {
      const normalizedFormat = String(formatFilterValue);
      chips.push({
        key: `format:${formatFilterValue}`,
        label: `Format: ${
          TEMPLATE_FORMAT_LABELS[normalizedFormat] ?? formatFilterValue
        }`,
        onClear: () =>
          onFormatFilterChange(
            formatFilterValues.filter((value) => value !== formatFilterValue),
          ),
      });
    });

    if (defaultFilterValue !== undefined) {
      chips.push({
        key: 'default',
        label: defaultFilterValue ? 'Default only' : 'Not default',
        onClear: () => onDefaultFilterChange(undefined),
      });
    }

    return chips;
  }, [
    archivedOnly,
    categoryFilterValues,
    defaultFilterValue,
    firmFilterValues,
    firmNames,
    formatFilterValues,
    onArchivedOnlyChange,
    onCategoryFilterChange,
    onDefaultFilterChange,
    onFirmFilterChange,
    onFormatFilterChange,
    onScopeFilterChange,
    onSearchChange,
    scopeFilterValue,
    searchValue,
  ]);

  const columns = useMemo<ColumnDef<ReadTemplateSummary>[]>(
    () => [
      {
        id: 'select',
        size: 44,
        minSize: 44,
        maxSize: 44,
        enableResizing: false,
        header: ({ table }) => {
          const rowIds = table
            .getRowModel()
            .rows.map((row) => String(row.original.id));
          const selectedInPage = rowIds.filter((rowId) =>
            selectedRows.has(rowId),
          ).length;

          return (
            <SelectionCheckbox
              checked={rowIds.length > 0 && selectedInPage === rowIds.length}
              indeterminate={
                selectedInPage > 0 && selectedInPage < rowIds.length
              }
              onChange={(checked) => {
                if (checked) {
                  onSelectionChange(new Set(rowIds));
                } else {
                  onSelectionChange(new Set());
                }
              }}
              ariaLabel="Select all visible templates"
              disabled={isLoading || rowIds.length === 0}
            />
          );
        },
        cell: ({ row }) => {
          const rowId = String(row.original.id);

          return (
            <SelectionCheckbox
              checked={selectedRows.has(rowId)}
              onChange={(checked) => {
                const nextSelection = new Set(selectedRows);

                if (checked) {
                  nextSelection.add(rowId);
                } else {
                  nextSelection.delete(rowId);
                }

                onSelectionChange(nextSelection);
              }}
              ariaLabel={`Select template ${row.original.name}`}
              disabled={isLoading}
            />
          );
        },
      },
      {
        id: 'name',
        accessorFn: (row) => row.display_name || row.name,
        size: 360,
        minSize: 220,
        header: () => (
          <HeaderSort
            label="Name"
            sortKey="name"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        ),
        cell: ({ row }) => {
          const displayName = row.original.display_name || row.original.name;
          const hasSecondaryName =
            row.original.display_name &&
            row.original.display_name !== row.original.name;
          const isArchived = isArchivedLikeStatus(row.original.status);
          const archivedLabel = getArchivedStatusLabel(row.original.status);

          return (
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <div
                  className={cn(
                    'truncate text-sm font-medium text-zinc-900 dark:text-zinc-100',
                    isArchived && 'text-zinc-700 dark:text-zinc-200',
                  )}
                  title={displayName}
                >
                  {displayName}
                </div>
                {isArchived && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-200">
                    <ArchiveBoxIcon className="h-3 w-3" />
                    {archivedLabel}
                  </span>
                )}
              </div>
              {hasSecondaryName && (
                <div
                  className="truncate text-xs text-zinc-500"
                  title={row.original.name}
                >
                  {row.original.name}
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: 'firm',
        accessorFn: (row) => {
          if (isGlobalTemplate(row)) {
            return GLOBAL_FIRM_FILTER_VALUE;
          }
          return row.firm_id ? String(row.firm_id) : '';
        },
        size: 210,
        minSize: 150,
        header: () => (
          <HeaderSort
            label="Firm"
            sortKey="firm"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            filterControl={
              firmOptions.length > 0 ? (
                <HeaderMultiFilter
                  label="Firm"
                  options={firmOptions}
                  selectedValues={firmFilterValues}
                  onChange={onFirmFilterChange}
                  searchable
                />
              ) : null
            }
          />
        ),
        cell: ({ row }) => {
          if (isGlobalTemplate(row.original)) {
            return (
              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                Global
              </span>
            );
          }

          if (!row.original.firm_id) {
            return <span className="text-zinc-400">-</span>;
          }

          const firmName = firmNames[String(row.original.firm_id)];

          return (
            <span className="truncate text-sm text-zinc-700 dark:text-zinc-300">
              {firmName || String(row.original.firm_id)}
            </span>
          );
        },
      },
      {
        id: 'category',
        accessorFn: (row) => row.category,
        size: 190,
        minSize: 140,
        header: () => (
          <HeaderSort
            label="Category"
            sortKey="category"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            filterControl={
              <HeaderMultiFilter
                label="Category"
                options={categoryFilterOptions}
                selectedValues={categoryFilterValues}
                onChange={(values) =>
                  onCategoryFilterChange(
                    values as NonNullable<TemplateListApiArg['category']>[],
                  )
                }
              />
            }
          />
        ),
        cell: ({ row }) => {
          const category = String(row.original.category ?? '');
          return (
            <span className="text-sm text-zinc-700 dark:text-zinc-300">
              {TEMPLATE_CATEGORY_LABELS[category] ?? row.original.category}
            </span>
          );
        },
      },
      {
        id: 'format',
        accessorFn: (row) => row.format,
        size: 120,
        minSize: 96,
        header: () => (
          <HeaderSort
            label="Format"
            sortKey="format"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            filterControl={
              <HeaderMultiFilter
                label="Format"
                options={formatFilterOptions}
                selectedValues={formatFilterValues}
                onChange={(values) =>
                  onFormatFilterChange(
                    values as NonNullable<TemplateListApiArg['templateFormat']>[],
                  )
                }
              />
            }
          />
        ),
        cell: ({ row }) => renderFormatBadge(row.original.format),
      },
      {
        id: 'is_default',
        accessorFn: (row) => row.is_default,
        size: 112,
        minSize: 88,
        header: () => (
          <HeaderSort
            label="Default"
            sortKey="is_default"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            filterControl={
              <HeaderSingleFilter
                label="Default"
                options={DEFAULT_FILTER_OPTIONS}
                selectedValue={
                  defaultFilterValue === undefined
                    ? null
                    : String(defaultFilterValue)
                }
                onChange={(value) => {
                  if (!value) {
                    onDefaultFilterChange(undefined);
                    return;
                  }
                  onDefaultFilterChange(value === 'true');
                }}
              />
            }
          />
        ),
        cell: ({ row }) => {
          const isDefault = row.original.is_default;

          if (!onToggleDefault) {
            return isDefault ? (
              <StarIcon className="h-4 w-4 text-amber-500" />
            ) : null;
          }

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleDefault(row.original);
              }}
              className="rounded p-0.5 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
              title={
                isDefault ? 'Unset default template' : 'Set default template'
              }
              data-row-action="true"
            >
              {isDefault ? (
                <StarIcon className="h-4 w-4 text-amber-500" />
              ) : (
                <StarOutlineIcon className="h-4 w-4 text-zinc-400" />
              )}
            </button>
          );
        },
      },
      {
        id: 'signatures',
        accessorFn: (row) => row.requires_signatures,
        size: 110,
        minSize: 88,
        enableResizing: false,
        header: () => (
          <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
            Signatures
          </span>
        ),
        cell: ({ row }) => {
          if (!row.original.requires_signatures) {
            return <span className="text-zinc-400">—</span>;
          }

          return (
            <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
              Signed
            </span>
          );
        },
      },
      {
        id: 'modified_at',
        accessorFn: (row) => row.modified_at,
        size: 150,
        minSize: 132,
        header: () => (
          <HeaderSort
            label="Modified"
            sortKey="modified_at"
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        ),
        cell: ({ row }) => renderDate(row.original.modified_at),
      },
    ],
    [
      categoryFilterOptions,
      categoryFilterValues,
      defaultFilterValue,
      firmFilterValues,
      firmNames,
      firmOptions,
      formatFilterOptions,
      formatFilterValues,
      isLoading,
      onCategoryFilterChange,
      onDefaultFilterChange,
      onFirmFilterChange,
      onFormatFilterChange,
      onSelectionChange,
      onSortChange,
      onToggleDefault,
      selectedRows,
      sortDirection,
      sortField,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 140,
      minSize: 88,
      maxSize: 700,
    },
    state: {
      columnSizing,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex =
    totalCount === 0 ? 0 : Math.min(currentPage * pageSize, totalCount);

  const handleRowClick = useCallback(
    (row: ReadTemplateSummary, event: ReactMouseEvent<HTMLTableRowElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-row-action="true"]')) {
        return;
      }

      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        window.open(
          getTemplateDetailsPath(row, detailSearch),
          '_blank',
          'noopener,noreferrer',
        );
        return;
      }

      onRowClick(row);
    },
    [detailSearch, onRowClick],
  );

  const handleRowAuxClick = useCallback(
    (row: ReadTemplateSummary, event: ReactMouseEvent<HTMLTableRowElement>) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-row-action="true"]')) {
        return;
      }

      if (event.button !== 1) {
        return;
      }

      event.preventDefault();
      window.open(
        getTemplateDetailsPath(row, detailSearch),
        '_blank',
        'noopener,noreferrer',
      );
    },
    [detailSearch],
  );

  const visibleColumnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-col gap-1.5 border-b border-zinc-200 bg-zinc-50/50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {title}
            </h2>
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
              {subtitle}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onArchivedOnlyChange(!archivedOnly)}
              aria-pressed={archivedOnly}
              className={cn(
                'h-8 border-zinc-200 text-xs text-zinc-600 hover:text-zinc-800 dark:border-zinc-700 dark:text-zinc-300 dark:hover:text-zinc-100',
                archivedOnly &&
                  'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200 dark:hover:bg-amber-500/25',
              )}
            >
              <ArchiveBoxIcon className="h-3.5 w-3.5" />
              Show archived
            </Button>
            <NewTemplateMenu firmId={firmId} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[220px] max-w-md flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-zinc-400" />
              <Input
                type="search"
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search templates..."
                className="h-8 bg-white pl-8 text-xs dark:bg-zinc-900"
              />
            </div>
          </div>

          {onScopeFilterChange && (
            <div className="inline-flex items-center overflow-hidden rounded-md border border-zinc-200 bg-white p-0.5 dark:border-zinc-700 dark:bg-zinc-900">
              <button
                type="button"
                onClick={() => onScopeFilterChange(undefined)}
                className={cn(
                  'inline-flex h-6 items-center rounded px-2 text-[11px] font-medium transition-colors',
                  !scopeFilterValue
                    ? 'bg-zinc-200 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100'
                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100',
                )}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => onScopeFilterChange('GLOBAL')}
                className={cn(
                  'inline-flex h-6 items-center gap-1 rounded px-2 text-[11px] font-medium transition-colors',
                  scopeFilterValue === 'GLOBAL'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200'
                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100',
                )}
              >
                <GlobeAltIcon className="h-3.5 w-3.5" />
                Global
              </button>
              <button
                type="button"
                onClick={() => onScopeFilterChange('FIRM')}
                className={cn(
                  'inline-flex h-6 items-center gap-1 rounded px-2 text-[11px] font-medium transition-colors',
                  scopeFilterValue === 'FIRM'
                    ? 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200'
                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100',
                )}
              >
                <BuildingOffice2Icon className="h-3.5 w-3.5" />
                Firm
              </button>
            </div>
          )}

          <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
            {totalCount > 0
              ? `Showing ${startIndex}-${endIndex} of ${totalCount}`
              : 'No templates'}
          </span>

          {activeFilterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onClear}
                  className="inline-flex h-6 items-center gap-1 rounded-md border border-zinc-200 bg-zinc-100 px-2 text-[11px] font-medium text-zinc-700 transition-colors hover:bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                >
                  <span className="max-w-[180px] truncate">{chip.label}</span>
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              ))}
              {canClearFilters && onClearFilters && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="h-6 px-2 text-[11px] text-zinc-600 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100"
                >
                  Clear all
                </Button>
              )}
            </div>
          )}

          {selectedRows.size > 0 && (
            <>
              <span className="text-[11px] font-medium text-zinc-700 dark:text-zinc-300">
                {selectedRows.size} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkDownloadClick}
                disabled={isBulkDownloading}
                className="h-8 text-xs"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                {isBulkDownloading ? 'Downloading...' : 'Download Selected'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onBulkArchiveClick}
                disabled={isBulkArchiving || isBulkDownloading}
                className="h-8 text-xs"
              >
                <ArchiveBoxIcon className="h-3.5 w-3.5" />
                {isBulkArchiving ? 'Archiving...' : 'Archive Selected'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div
          ref={tableContainerRef}
          className="h-full overflow-auto"
          style={{ contain: 'layout' }}
        >
          <table
            className="w-full min-w-full table-fixed border-separate border-spacing-0"
            style={{ width: table.getTotalSize(), minWidth: '100%' }}
          >
            <thead className="sticky top-0 z-10 bg-zinc-50/95 backdrop-blur-sm dark:bg-zinc-900/95">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-b border-zinc-200 dark:border-zinc-800"
                >
                  {headerGroup.headers.map((header, headerIndex) => {
                    const canResize = header.column.getCanResize();
                    const isLastHeader =
                      headerIndex === headerGroup.headers.length - 1;

                    return (
                      <th
                        key={header.id}
                        className={cn(
                          'relative border-b border-zinc-200 px-2.5 py-1 text-left align-middle dark:border-zinc-800',
                          !isLastHeader &&
                            'border-r border-zinc-100 dark:border-zinc-800/80',
                        )}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                        {canResize && (
                          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                          <div
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={cn(
                              'absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none',
                              header.column.getIsResizing()
                                ? 'bg-blue-500/50'
                                : 'hover:bg-zinc-300/60 dark:hover:bg-zinc-600/70',
                            )}
                            data-row-action="true"
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: Math.min(pageSize, 8) }).map(
                  (_, index) => (
                    <tr
                      key={`skeleton-${index}`}
                      className="h-10 border-b border-zinc-100 dark:border-zinc-800"
                    >
                      {table.getVisibleLeafColumns().map((column) => (
                        <td
                          key={`${column.id}-${index}`}
                          className="px-2.5 py-1.5"
                        >
                          <div className="h-3.5 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                        </td>
                      ))}
                    </tr>
                  ),
                )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={visibleColumnCount}
                    className="px-4 py-10 text-center text-sm text-zinc-500"
                  >
                    No templates found.
                  </td>
                </tr>
              )}

              {!isLoading && rows.length > 0 && (
                <>
                  {paddingTop > 0 && (
                    <tr>
                      <td
                        style={{ height: paddingTop }}
                        colSpan={visibleColumnCount}
                      />
                    </tr>
                  )}

                  {virtualRows.map((virtualRow) => {
                    const row = rows.at(virtualRow.index);
                    if (!row) {
                      return null;
                    }
                    const rowKey = String(row.original.id);
                    const isArchived = isArchivedLikeStatus(
                      row.original.status,
                    );
                    const rowClassName = cn(
                      'h-10 cursor-pointer border-b border-zinc-100 text-[13px] leading-5 transition-colors hover:bg-zinc-100/70 dark:border-zinc-800 dark:hover:bg-zinc-800/80',
                      virtualRow.index % 2 === 1 &&
                        'bg-zinc-50/35 dark:bg-zinc-900/25',
                      selectedRows.has(rowKey) &&
                        'bg-blue-50/70 dark:bg-blue-500/15',
                      isArchived &&
                        !selectedRows.has(rowKey) &&
                        'bg-amber-50/65 text-zinc-700 hover:bg-amber-100/80 dark:bg-amber-500/10 dark:text-zinc-200 dark:hover:bg-amber-500/20',
                    );

                    const rowCells = row
                      .getVisibleCells()
                      .map((cell, cellIndex, cells) => {
                        const cellClassName = cn(
                          'max-w-0 px-2.5 py-1.5 align-middle text-[13px] leading-5',
                          cellIndex < cells.length - 1 &&
                            'border-r border-zinc-100/80 dark:border-zinc-800/80',
                          isArchived &&
                            cellIndex === 0 &&
                            'border-l-2 border-l-amber-300 dark:border-l-amber-400/80',
                        );

                        if (cell.column.id === 'name') {
                          return (
                            <HoverCardTrigger key={cell.id} asChild>
                              <td
                                className={cellClassName}
                                style={{ width: cell.column.getSize() }}
                              >
                                <div className="truncate">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </div>
                              </td>
                            </HoverCardTrigger>
                          );
                        }

                        return (
                          <td
                            key={cell.id}
                            className={cellClassName}
                            style={{ width: cell.column.getSize() }}
                          >
                            <div className="truncate">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          </td>
                        );
                      });

                    return (
                      <HoverCard key={rowKey} openDelay={140} closeDelay={120}>
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                        <tr
                          className={rowClassName}
                          title="Open template (Cmd/Ctrl-click or middle-click for new tab)"
                          onClick={(event) =>
                            handleRowClick(row.original, event)
                          }
                          onAuxClick={(event) =>
                            handleRowAuxClick(row.original, event)
                          }
                        >
                          {rowCells}
                        </tr>
                        <HoverCardContent
                          side="right"
                          align="start"
                          sideOffset={8}
                          collisionPadding={12}
                          className="pointer-events-none w-[360px] border-none bg-transparent p-0 shadow-none"
                        >
                          <TemplateRowPreviewCard
                            templateId={String(row.original.id)}
                            templateName={
                              row.original.display_name || row.original.name
                            }
                            previewRenderUrl={row.original.preview_render_url}
                            previewImageUrl={row.original.preview_image_url}
                            sampleRenderStatus={row.original.sample_render_status}
                          />
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}

                  {paddingBottom > 0 && (
                    <tr>
                      <td
                        style={{ height: paddingBottom }}
                        colSpan={visibleColumnCount}
                      />
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!isLoading && rows.length > 0 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={Math.max(1, totalPages)}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
